/* eslint-disable react/no-unescaped-entities */
import EligibilityList from "@/components/Affiliate/EligibilityList";
import MarketingList from "@/components/Affiliate/MarketingList";
import PaymentList from "@/components/Affiliate/PaymentList";
import ReferralsList from "@/components/Affiliate/ReferralsList";
import RepresentationsList from "@/components/Affiliate/RepresentationsList";
import TerminationList from "@/components/Affiliate/TerminationList";
import ListSection from "@/components/atoms/ListSection";
import TermsParagraph from "@/components/atoms/TermsParagraph";
import { FooterDataType } from "@/components/organisms/Footer";
import { PageTemplate } from "@/components/organisms/PageTemplate";
import { COMPANY_LEGAL_NAME } from "@/shared/constants/common.constants";
import { footerData_en, footerData_us } from "@/shared/data/footer.data";
import { footerData_kr } from "@/shared/data/footer.data.kr";
import { NavbarDataType, navbarData_en, navbarData_us } from "@/shared/data/navbar.data";
import { navbarData_kr } from "@/shared/data/navbar.data.kr";
import { ROUTE_PATHS } from "@/shared/data/routes";
import { LIST_TYPE } from "@/shared/types/atoms.types";
import { GetStaticProps, InferGetStaticPropsType } from "next";
import Head from "next/head";
import Link from "next/link";
import React from "react";

interface AffiliateProps {}

export const provisionsData = [
  {
    index: "(a)",
    title: "Modification, Amendment or Termination:",
    description:
      "Graphy may, in its sole discretion, modify or revise these Affiliate Terms, and You agree to be bound by such modifications or revisions. Your continued use of Your Affiliate account on the Platform post any modification of these Affiliate Terms shall be taken as Your consent and acceptance to such modifications. Nothing in these Affiliate Terms shall be deemed to confer any third-party rights or benefits. You are advised to check our Platform and the Affiliate Page frequently to see recent changes and to keep Yourself updated with the most recent updates.",
  },
  {
    index: "(b)",
    title: "Force Majeure:",
    description:
      "Graphy shall not be liable for failure to perform, or the delay in performance of, any of its obligations if, and to the extent that, such failure or delay is caused by events substantially beyond the its control, including but not limited to acts of God, acts of the public enemy or governmental body in its sovereign or contractual capacity, war, terrorism, floods, fire, strikes, epidemics, civil unrest or riots, power outage, and/or unusually severe weather.",
  },
  {
    index: "(c)",
    title: "Miscellaneous:",
    description:
      "If any part of these Affiliate Terms is found to be unlawful, void or unenforceable, that part of these Affiliate Terms will be deemed severable and will not affect the validity and enforceability of any remaining provisions. Any notice required to be given in connection with the Platform shall be in writing and sent to the registered office of Graphy. We do not guarantee continuous, uninterrupted or secure access to the Platform, and operation of the Platform may be interfered by numerous factors outside our control. Headings are for reference purpose only an on no way define, limit, construe or describe the scope or extent of such section. Out failure to act with respect to any breach by you or others does not waive our right to act with respect to subsequent or similar breaches.",
  },
];

export const getStaticProps: GetStaticProps<{
  navbarData: NavbarDataType;
  footerData: FooterDataType;
}> = async (context) => {
  const navbarData = {
    en: navbarData_en,
    us: navbarData_us,
    kr: navbarData_kr,
    in: navbarData_en,
  }[(context.locale as "en" | "kr" | "us" | "in") ?? "en"];

  const footerData = {
    en: footerData_en,
    us: footerData_us,
    kr: footerData_kr,
    in: footerData_en,
  }[(context.locale as "en" | "kr" | "us" | "in") ?? "en"];

  return {
    props: {
      navbarData,
      footerData,
    },
  };
};
export default function AffiliatePage({
  navbarData,
  footerData,
}: InferGetStaticPropsType<typeof getStaticProps>) {
  return (
    <PageTemplate navbarData={navbarData} footerData={footerData}>
      <div className="link-dash flex items-center justify-center px-1 md:px-6">
        <Head>
          <title>Graphy affiliate partner program terms and conditions</title>
          <meta
            name="description"
            content="Graphy affiliate partner program terms and conditions"
          />
          <meta
            name="viewport"
            content="initial-scale=1.0, width=device-width"
          />
        </Head>
        <div className="WordSection1 max-w-3xl">
          <h1 className="mx-auto mt-40 text-center text-xl font-extrabold text-black sm:text-2xl md:text-5xl">
            Graphy Affiliate Partner Program Terms and Conditions
          </h1>
          <h2 className="mb-10 pt-6 text-center text-sm uppercase tracking-ultrawide text-black md:text-xl">
            Last updated: DECEMBER 22, 2022
          </h2>

          <TermsParagraph>
            Graphy Affiliate Partner Program <b>(“Affiliate Program”)</b> is
            made available to you <b>(“You”, “Your”/ “Affiliate”)</b> through
            our Platform. Reference to the term <b>“Affiliate”</b> shall include
            any individual, or entity applying to or participating in the
            Affiliate Program.
          </TermsParagraph>

          <TermsParagraph>
            Access to and participation in our Affiliate Program is subject to
            the following terms and conditions <b>(“Affiliate Terms”)</b>. By
            participating in our Affiliate Program, You acknowledge that You
            have read, understood, and agree to be legally bound by the terms
            and conditions of these Affiliate Terms along with the Graphy’s{" "}
            <Link href={ROUTE_PATHS.LEGAL.TermsOfService}>
              Terms of Service
            </Link>
            , <Link href={ROUTE_PATHS.LEGAL.PrivacyPolicy}>Privacy Policy</Link>{" "}
            and any other terms and conditions updated on the Platform or
            communicated to You from time to time
            <b>(“Platform Terms”)</b>. Please take the time to read our Platform
            Terms. If You do not agree to any, then please do not participate in
            our Affiliate Program or use our service.
          </TermsParagraph>

          <TermsParagraph>
            Further, we may use third-party platform(s) to track the conversion
            and Affiliate performance, including specific referrals and
            commissions earned through the Affiliate Program{" "}
            <b>(“Affiliate Platform”)</b> and accordingly, You will be subject
            to the terms and conditions of such Affiliate Platform(s). Your
            acceptance of these Affiliate Terms shall be deemed as acceptance of
            such Affiliate Platform terms. Hence, we suggest that You kindly
            take the time to read such Affiliate Platform terms prior to signing
            up as an Affiliate.
          </TermsParagraph>

          <TermsParagraph>
            Capitalized terms not defined in these Affiliate Terms shall have
            the meaning set forth in our Platform Terms.
          </TermsParagraph>

          <TermsParagraph>
            We may change these Affiliate Terms from time to time, and will post
            any changes on the Platform or notify You via email, at our option,
            as soon as such changes are in effect.{" "}
          </TermsParagraph>

          <ListSection
            listIndex="1."
            listType={LIST_TYPE.LIST1}
            title="Eligibility and Participation:"
            description="Participation in the Affiliate Program is voluntary, and to participate in the Affiliate Program– "
          />

          <EligibilityList />

          <ListSection
            listIndex="2."
            listType={LIST_TYPE.LIST1}
            title="Referrals:"
          />

          <ReferralsList />

          <ListSection
            listIndex="3."
            listType={LIST_TYPE.LIST1}
            title="Marketing:"
          />

          <MarketingList />

          <ListSection
            listIndex="4."
            listType={LIST_TYPE.LIST1}
            title="Referral Rewards and Payment:"
          />

          <PaymentList />

          <ListSection
            listIndex="5."
            listType={LIST_TYPE.LIST1}
            title="Representations and Warranties:"
          />
          <TermsParagraph className="ml-4 md:ml-16">
            You hereby represent and warrant to us as follows:
          </TermsParagraph>

          <RepresentationsList />

          <ListSection
            listIndex="6."
            listType={LIST_TYPE.LIST1}
            title="Term and Termination:"
          />

          <TerminationList />

          <ListSection
            listIndex="7."
            listType={LIST_TYPE.LIST1}
            title="Indemnity and Release:"
          />

          <TermsParagraph className="ml-4 md:ml-16">
            You agree to indemnify and hold harmless Graphy and its successors
            and assigns and all of their respective officers, directors,
            subsidiaries, affiliates, agents and employees, from any claims,
            liability, judgments, awards, losses, obligations, costs, expenses
            or fees (including reasonable attorneys’ fees), demands, or actions
            made by any third party or penalty imposed due to or arising out of
            Your breach of these Affiliate Terms, Platform Terms, any terms as
            may be communicated to You in writing (emails included), and/or any
            document incorporated herein by reference, and/or arising out of any
            claim that content provided by You caused damage or loss to a third
            party, or Your violation of any law, rules, regulations or the
            rights of a third party.
          </TermsParagraph>

          <ListSection
            listIndex="8."
            listType={LIST_TYPE.LIST1}
            title="Office of Foreign Asset Control (“OFAC”):"
          />

          <TermsParagraph className="ml-4 md:ml-16">
            You represent and warrant that You are not, nor are You owned or
            controlled directly or indirectly by, any person, group, entity or
            nation named on any list issued by the Department of the Treasury's
            Office of Foreign Asset Control <b>("OFAC" )</b>, or any similar
            list or by any law, order, rule or regulation or any Executive Order
            of the President of the United States, including Executive Order
            13224, as a "Specially Designated National and Blocked Person",
            terrorist, or other banned or blocked person (any such person,
            group, entity or nation being hereinafter referred to as a{" "}
            <b>("Prohibited Person")</b>; (ii) You are not (nor are You owned or
            controlled, directly or indirectly, by any person, group, entity or
            nation which is) acting directly or indirectly for or on behalf of
            any Prohibited Person; (iii) You (and any person, group, or entity
            which You control, directly or indirectly) have not conducted nor
            will conduct business nor have engaged nor will engage in any
            transaction or dealing with any Prohibited Person in violation of
            the U.S. Patriot Act or any OFAC rule or regulation, including,
            without limitation, the making or receiving of any contribution of
            funds, good or services to or for the benefit of a Prohibited Person
            in violation of the U.S. Patriot Act or any OFAC rule or regulation;
            (iv) You are not prohibited by any sanctions program as maintained
            by OFAC from transacting with Graphy, including those sanctions
            programs currently in effect with respect to Cuba, Iran, North
            Korea, Syria, Venezuela, or Crimea. In connection with the
            foregoing, it is expressly understood and agreed that the
            representations and warranties contained in this subsection shall be
            continuing in nature.
          </TermsParagraph>

          <ListSection
            listIndex="9."
            listType={LIST_TYPE.LIST1}
            title="Governing Law, and Jurisdiction:"
          />

          <TermsParagraph className="ml-4 md:ml-16">
            These Affiliate Terms shall be governed by and construed in
            accordance with such laws and jurisdiction as provided under the
            Platform Terms.
          </TermsParagraph>

          <ListSection
            listIndex="10."
            listType={LIST_TYPE.LIST1}
            title="General Provisions:"
          />

          {provisionsData.map((data) => (
            <React.Fragment key={data.index}>
              <ListSection
                listIndex={data.index}
                listType={LIST_TYPE.LIST3}
                title={data.title}
                description={data.description}
              />
            </React.Fragment>
          ))}

          <ListSection
            listIndex="11."
            listType={LIST_TYPE.LIST1}
            title="Contact Us:"
          />

          <TermsParagraph className="ml-4 md:ml-16">
            If You’ve have concerns or queries regarding the Agreement, You may
            write to us by email at{" "}
            <a href="mailto:legal@graphy.com">legal@graphy.com</a> or by post
            to:
          </TermsParagraph>

          <TermsParagraph className="ml-4 md:ml-16">
            <b className="mt-12 block">{COMPANY_LEGAL_NAME}</b>
            <br />
            Maruti Infotech Centre, 2nd Floor, B-Block,
            <br />
            <br />
            Domlur, Koramangala Inner Ring Road,
            <br />
            <br />
            Bangalore- 560 071, Karnataka, India.
          </TermsParagraph>
        </div>
      </div>
    </PageTemplate>
  );
}
